import { useCallback, useEffect, useState } from "react";
import QRCode from "qrcode";
import {
  TenantApiError,
  type TenantBusinessMembership,
  type TenantCurrentBusiness,
  type TenantMeResponse,
} from "@qr-menu/api-tenant";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DetailItem,
  DetailList,
  RowActions,
  RowConfirm,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";
import { writeTenantToken } from "../lib/session";

// On-screen preview stays small and cosmetic; the download is sized for
// print. Both encode business.slug alone (already public, already shown in
// plaintext elsewhere on this page) — never a token or other tenant-internal
// value.
const QR_PREVIEW_SIZE = 112;
const QR_DOWNLOAD_SIZE = 1024;

type MeState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: TenantMeResponse };

type BizState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: TenantBusinessMembership[] };

type PublishState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; business: TenantCurrentBusiness };

// A build-time origin takes precedence when configured, so a real deployment
// can point this at wherever the public menu app actually lives. In local dev
// the tenant panel and public menu run as two separate Vite dev servers on
// fixed ports; this maps between them so "Aç"/"Kopyala" can offer a real,
// openable URL there. Outside that pairing, with no configured origin, this
// bundle has no way to know the public app's actual domain, so callers fall
// back to the bare path instead of guessing a wrong absolute URL.
function resolvePublicOrigin(): string | null {
  const raw = import.meta.env.VITE_PUBLIC_MENU_ORIGIN;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed !== "") return trimmed.replace(/\/+$/, "");
  }

  if (typeof window === "undefined") return null;
  return window.location.origin === "http://localhost:5174"
    ? "http://localhost:5175"
    : null;
}

// A slug is already URL-safe by construction, but this is a filesystem name,
// not a URL segment, so it gets its own narrow allowlist rather than reusing
// that assumption.
function safeFileSlug(slug: string): string {
  const cleaned = slug.replace(/[^a-zA-Z0-9-_]/g, "-");
  return cleaned === "" ? "menu" : cleaned;
}

export function HomeRoute() {
  const [me, setMe] = useState<MeState>({ kind: "loading" });
  const [biz, setBiz] = useState<BizState>({ kind: "loading" });
  const [switchingId, setSwitchingId] = useState<number | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const [publish, setPublish] = useState<PublishState>({ kind: "loading" });
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);

  const loadCurrentBusiness = useCallback(async () => {
    try {
      const business = await tenantApi.currentBusiness();
      setPublish({ kind: "ready", business });
    } catch (err) {
      setPublish({
        kind: "error",
        message: safeMessage(err, "İşletme bilgileri alınamadı."),
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await tenantApi.me();
        if (!cancelled) setMe({ kind: "ready", data });
      } catch (err) {
        if (cancelled) return;
        setMe({ kind: "error", message: describe(err, "Bilgi alınamadı.") });
      }

      try {
        const items = await tenantApi.businesses();
        if (!cancelled) setBiz({ kind: "ready", items });
      } catch (err) {
        if (cancelled) return;
        setBiz({
          kind: "error",
          message: describe(err, "İşletme listesi alınamadı."),
        });
      }

      if (!cancelled) await loadCurrentBusiness();
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [loadCurrentBusiness]);

  async function handleSwitch(id: number) {
    if (switchingId !== null || publishing) return;
    setSwitchingId(id);
    setSwitchError(null);
    setPublishError(null);
    setPublishMessage(null);
    try {
      const res = await tenantApi.switchBusiness(id);
      // Write the new token BEFORE any subsequent protected request runs.
      writeTenantToken(res.access_token);
      setMe({
        kind: "ready",
        data: {
          user: res.user,
          active_business: res.active_business,
          subscription: res.subscription,
        },
      });
      // The publish card describes the active business, so it must follow the
      // switch rather than keep showing the previous tenant's state.
      setPublish({ kind: "loading" });
      await loadCurrentBusiness();
    } catch (err) {
      // 401 already triggered the client's onUnauthorized; other failures
      // keep the current token and active business untouched.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setSwitchError(describe(err, "İşletme değiştirilemedi."));
      }
    } finally {
      setSwitchingId(null);
    }
  }

  async function handleTogglePublish(next: boolean) {
    if (publishing || switchingId !== null) return;

    setPublishing(true);
    setPublishError(null);
    setPublishMessage(null);
    try {
      await tenantApi.setBusinessPublished({ is_published: next });
      // Refetch rather than patching the flag locally, so the card reflects
      // what the backend actually stored.
      await loadCurrentBusiness();
      setPublishMessage(next ? "Menü yayınlandı." : "Menü yayından kaldırıldı.");
    } catch (err) {
      // 401 already triggered the client's onUnauthorized; other failures keep
      // the current state and skip the refetch.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setPublishError(safeMessage(err, "Menü durumu güncellenemedi."));
      }
    } finally {
      setPublishing(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Hesabım</CardTitle>
          <CardDescription>
            <code>GET /api/v1/users/me</code> — aktif oturum özeti.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {me.kind === "loading" && <div className="state">Yükleniyor…</div>}
          {me.kind === "error" && (
            <div className="state error">Yüklenemedi. {me.message}</div>
          )}
          {me.kind === "ready" && <MeBody data={me.data} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Menü Durumu</CardTitle>
          <CardDescription>
            Yayındaki menü QR bağlantısı üzerinden herkese açıktır.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {publish.kind === "loading" && (
            <div className="state">Yükleniyor…</div>
          )}
          {publish.kind === "error" && (
            <div className="state error">Yüklenemedi. {publish.message}</div>
          )}
          {publish.kind === "ready" && (
            <PublishBody
              business={publish.business}
              publishing={publishing}
              disabled={switchingId !== null}
              onToggle={handleTogglePublish}
            />
          )}
          {publishError && <div className="state error">{publishError}</div>}
          {publishMessage && <div className="state ok">{publishMessage}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>İşletmelerim</CardTitle>
          <CardDescription>
            <code>GET /api/v1/businesses</code> — erişebildiğiniz üyelikler.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {biz.kind === "loading" && <div className="state">Yükleniyor…</div>}
          {biz.kind === "error" && (
            <div className="state error">Yüklenemedi. {biz.message}</div>
          )}
          {biz.kind === "ready" && (
            <BusinessesBody
              items={biz.items}
              activeId={
                me.kind === "ready" ? me.data.active_business.id : null
              }
              switchingId={switchingId}
              onSwitch={handleSwitch}
              error={switchError}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}

function MeBody({ data }: { data: TenantMeResponse }) {
  const { user, active_business: biz } = data;
  const fullName = [user.name, user.surname].filter(Boolean).join(" ");

  return (
    <DetailList>
      <DetailItem label="Ad">
        {fullName || <span className="hint">—</span>}
      </DetailItem>
      <DetailItem label="E-posta">{user.email}</DetailItem>
      <DetailItem label="Aktif işletme">
        {biz.name} <code style={{ marginLeft: 6 }}>{biz.slug}</code>
      </DetailItem>
      <DetailItem label="Rol">
        <Badge>{biz.role}</Badge>
      </DetailItem>
    </DetailList>
  );
}

interface PublishBodyProps {
  business: TenantCurrentBusiness;
  publishing: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}

function PublishBody({
  business,
  publishing,
  disabled,
  onToggle,
}: PublishBodyProps) {
  const isPublished = business.is_published;
  const hasSlug = business.slug.trim() !== "";
  const showPublicLink = isPublished && hasSlug;

  const publicOrigin = resolvePublicOrigin();
  const publicPath = hasSlug ? `/menu/${business.slug}` : "";
  const publicHref = publicOrigin ? `${publicOrigin}${publicPath}` : publicPath;
  // A bare path has no meaning to a phone camera with no browser tab to
  // resolve it against, unlike "Aç"/"Kopyala" which still work from a
  // relative path inside this app's own origin. Only offer the QR once the
  // href is a real absolute URL.
  const showQr = showPublicLink && publicOrigin !== null;

  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const [downloadError, setDownloadError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Clears stale feedback from a previous copy/download so neither reappears
  // when the row is hidden (unpublish) and later shown again (republish)
  // without a fresh click.
  useEffect(() => {
    setCopyState("idle");
    setDownloadError(false);
  }, [showPublicLink, publicHref]);

  type QrPreviewState =
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; dataUrl: string }
    | { kind: "error" };

  const [qrPreview, setQrPreview] = useState<QrPreviewState>({ kind: "idle" });

  useEffect(() => {
    if (!showQr) {
      setQrPreview({ kind: "idle" });
      return;
    }
    let cancelled = false;
    setQrPreview({ kind: "loading" });
    QRCode.toDataURL(publicHref, { width: QR_PREVIEW_SIZE, margin: 1 })
      .then((dataUrl) => {
        if (!cancelled) setQrPreview({ kind: "ready", dataUrl });
      })
      .catch(() => {
        if (!cancelled) setQrPreview({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [showQr, publicHref]);

  async function handleCopy() {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      setCopyState("failed");
      return;
    }
    try {
      await navigator.clipboard.writeText(publicHref);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  async function handleDownloadQr() {
    if (downloading) return;
    setDownloadError(false);
    setDownloading(true);
    try {
      // A higher error correction level than the preview's default: this
      // copy is meant to be printed and scanned off paper, where smudges,
      // folds or glare are more likely than on a screen.
      const dataUrl = await QRCode.toDataURL(publicHref, {
        width: QR_DOWNLOAD_SIZE,
        errorCorrectionLevel: "H",
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `menu-qr-${safeFileSlug(business.slug)}.png`;
      link.click();
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <RowActions>
        <RowConfirm>
          <Badge variant={isPublished ? "success" : "danger"}>
            {isPublished ? "Yayında" : "Taslak"}
          </Badge>
          <code>{business.slug}</code>
          <Button
            type="button"
            variant={isPublished ? "default" : "primary"}
            onClick={() => onToggle(!isPublished)}
            disabled={publishing || disabled}
          >
            {publishing
              ? "Kaydediliyor…"
              : isPublished
                ? "Yayından kaldır"
                : "Menüyü yayınla"}
          </Button>
        </RowConfirm>
      </RowActions>

      {showPublicLink ? (
        <>
          <RowActions style={{ marginTop: 8 }}>
            <RowConfirm>
              <code className="wrap-cell">{publicHref}</code>
              <a
                className="button-link"
                href={publicHref}
                target="_blank"
                rel="noreferrer"
              >
                Aç
              </a>
              <Button type="button" onClick={handleCopy}>
                Kopyala
              </Button>
              {showQr && (
                <Button
                  type="button"
                  onClick={handleDownloadQr}
                  disabled={downloading}
                >
                  {downloading ? "Hazırlanıyor…" : "QR indir"}
                </Button>
              )}
              {copyState === "copied" && (
                <span className="hint">Kopyalandı</span>
              )}
              {copyState === "failed" && (
                <span className="hint">Kopyalanamadı</span>
              )}
              {downloadError && (
                <span className="hint">QR oluşturulamadı.</span>
              )}
            </RowConfirm>
          </RowActions>

          {showQr ? (
            <div className="qr-preview-row">
              {qrPreview.kind === "ready" && (
                <img
                  className="qr-preview"
                  src={qrPreview.dataUrl}
                  width={QR_PREVIEW_SIZE}
                  height={QR_PREVIEW_SIZE}
                  alt={`${business.slug} genel menüsü için QR kodu`}
                />
              )}
              {qrPreview.kind === "loading" && (
                <span className="hint">QR hazırlanıyor…</span>
              )}
              {qrPreview.kind === "error" && (
                <span className="hint">QR oluşturulamadı.</span>
              )}
            </div>
          ) : (
            <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
              QR indirme için public menü origin'i yapılandırılmalı.
            </p>
          )}
        </>
      ) : (
        <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
          {isPublished
            ? "Genel menü bağlantısı için işletme slug'ı eksik."
            : "Genel menü bağlantısı, menü yayınlandığında burada görünecek."}
        </p>
      )}
    </>
  );
}

interface BusinessesBodyProps {
  items: TenantBusinessMembership[];
  activeId: number | null;
  switchingId: number | null;
  onSwitch: (id: number) => void;
  error: string | null;
}

function BusinessesBody({
  items,
  activeId,
  switchingId,
  onSwitch,
  error,
}: BusinessesBodyProps) {
  if (items.length === 0) {
    return <div className="state">Üye olduğunuz bir işletme yok.</div>;
  }

  return (
    <>
      {error && <div className="state error">{error}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableCell as="th">İşletme</TableCell>
            <TableCell as="th">Slug</TableCell>
            <TableCell as="th">Rol</TableCell>
            <TableCell as="th" style={{ width: 200 }} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((m) => {
            const isActive = activeId != null && m.id === activeId;
            const isSwitching = switchingId === m.id;
            return (
              <TableRow key={m.id}>
                <TableCell>{m.name}</TableCell>
                <TableCell>
                  <code>{m.slug}</code>
                </TableCell>
                <TableCell>
                  <Badge>{m.role}</Badge>
                </TableCell>
                <RowActions as="td">
                  <RowConfirm>
                    {isActive ? (
                      <Badge variant="success">Aktif</Badge>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => onSwitch(m.id)}
                        disabled={switchingId !== null}
                      >
                        {isSwitching ? "Değişiyor…" : "Bu işletmeye geç"}
                      </Button>
                    )}
                  </RowConfirm>
                </RowActions>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
}

function describe(err: unknown, fallback: string): string {
  if (err instanceof TenantApiError) {
    return `${err.code}: ${err.message}`;
  }
  return fallback;
}

// The publish card is user-facing rather than a contract probe, so it shows the
// backend's curated message alone, without the error code.
function safeMessage(err: unknown, fallback: string): string {
  if (err instanceof TenantApiError) {
    return err.message;
  }
  return fallback;
}
